import isFieldAnswered from './isFieldAnswered';
import matrixRowsComplete from './matrixRowsComplete';

// De poort voor verplichte vragen in de open vragenlijst.
//
// Eén helper voor de twee plekken die hem nodig hebben: een losse vraag
// (TopLevelFields) en een sectie met meerdere vragen op één pagina (Section).
// Die sectieweergave is geen randgeval — hij is de standaard zodra een
// vragenlijst secties heeft, en daar ontbrak de controle aanvankelijk helemaal.
// Twee kopieën zouden alsnog uit elkaar lopen.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/** Waarom de respondent blijft staan. De aanroeper maakt er een tekst van. */
const REQUIRED_BLOCK = {
    VERPLICHT: 'verplicht',
    ALLE_RIJEN: 'alle-rijen',
    CONTROLE_MISLUKT: 'controle-mislukt',
};

const isMatrixType = (type) => type === 'matrix-one' || type === 'matrix-multiple';

/**
 * Of deze vraag om álle rijen vraagt: een meetstandaard-vraag is dat altijd,
 * een zelfgemaakte matrixvraag zodra de opsteller haar verplicht stelde.
 *
 * @param {{Type?: string, MSIId?: string, Required?: *}} field
 * @returns {boolean}
 */
const vraagtOmAlleRijen = (field) =>
    isMatrixType(field?.Type) && (Boolean(field?.MSIId) || field?.Required === true);

/**
 * @callback LoadMatrixRows
 * @param {string} fieldID - ID van de matrixvraag
 * @returns {Promise<Array<{ID: string, Deleted?: boolean|string}>>} De rijen van die vraag
 */

/**
 * @typedef {object} RequiredBlock
 * @property {string} reason - Een waarde uit REQUIRED_BLOCK
 * @property {string} fieldID - ID van de vraag die de respondent tegenhoudt
 */

/**
 * Loopt de vragen af en geeft de eerste vraag die de respondent tegenhoudt,
 * met de reden. Een sectie heeft de vraag nodig om de melding erbij te zetten
 * (testronde 2026-10-28, B2).
 *
 * Valt bewust DICHT als de rijen niet op te halen zijn of de lader geen lijst
 * teruggeeft: 'ik weet het niet' mag hier niet als 'niets aan de hand' gelden,
 * anders staat de poort open precies wanneer de gegevens ontbreken.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * @param {Array<object>} [fields] - De vragen die op deze stap staan
 * @param {Array<{FieldID?: string, Input?: *, RowID?: string}>} [responses] - Alle antwoorden uit de context
 * @param {LoadMatrixRows} loadMatrixRows - Haalt de rijen van één matrixvraag op
 * @returns {Promise<RequiredBlock|null>} De blokkade, of null als de respondent door mag
 */
const firstRequiredBlock = async (fields, responses, loadMatrixRows) => {
    for (const field of fields || []) {
        if (!field) continue;

        const block = (reason) => ({ reason, fieldID: field.ID });
        const fieldResponses = (responses || []).filter((r) => r?.FieldID === field.ID);

        if (vraagtOmAlleRijen(field)) {
            let rows;
            try {
                rows = await loadMatrixRows(field.ID);
            } catch {
                return block(REQUIRED_BLOCK.CONTROLE_MISLUKT);
            }
            if (!Array.isArray(rows)) return block(REQUIRED_BLOCK.CONTROLE_MISLUKT);
            if (!matrixRowsComplete(fieldResponses, rows).complete) return block(REQUIRED_BLOCK.ALLE_RIJEN);
            continue;
        }

        if (field.Required === true && !isFieldAnswered(field, fieldResponses)) {
            return block(REQUIRED_BLOCK.VERPLICHT);
        }
    }

    return null;
};

/**
 * Alleen de reden — voor een stap met één vraag, waar de vraag vaststaat.
 *
 * @param {Array<object>} [fields]
 * @param {Array<{FieldID?: string, Input?: *, RowID?: string}>} [responses]
 * @param {LoadMatrixRows} loadMatrixRows
 * @returns {Promise<string|null>} Een waarde uit REQUIRED_BLOCK, of null als de respondent door mag
 */
const requiredFieldsBlock = async (fields, responses, loadMatrixRows) =>
    (await firstRequiredBlock(fields, responses, loadMatrixRows))?.reason ?? null;

export { REQUIRED_BLOCK, firstRequiredBlock };
export default requiredFieldsBlock;
